import * as React from 'react';
import { AutoScaledImage } from '@helpcenter/auto-scaled-image';
import { BreadCrumbs } from 'view/common/bread-crumbs';
import { CsmBreadcrumbs } from 'view/common/csm-breadcrumbs';
import {
	SubHeadingContainer,
	IconContainer,
	SubHeading,
	Description,
} from 'view/common/paper-header/styled';
import Heading from '@atlaskit/heading/heading';
import { UseTypographyMigratedComponent } from '@atlassian/help-center-common-util/enable-typography-migration';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';

export interface PaperHeaderProps {
	subHeading: string | undefined;
	description: string | undefined;
	logoUrl?: string | undefined;
	portalId?: number;
	categoryId?: string;
	showRequestsInBreadcrumbs?: boolean;
	dangerouslyAllowHtmlInDescription?: boolean;
	requestKey?: string | undefined;
	requestUrl?: string | undefined;
	dataTestId?: string;
	additionalBreadCrumbs?: React.ReactElement[];
	/** When provided, renders a dynamic ancestor-based breadcrumb trail fetched by article ID. */
	articleId?: string;
	hideBreadcrumbs?: boolean;
}

const headerDescription = (description: string, dangerouslyAllowHtmlInDescription?: boolean) =>
	dangerouslyAllowHtmlInDescription ? (
		<Description dangerouslySetInnerHTML={{ __html: description }} />
	) : (
		<Description>{description}</Description>
	);

const PaperHeader: React.FC<PaperHeaderProps> = ({
	subHeading,
	description,
	logoUrl,
	showRequestsInBreadcrumbs,
	dangerouslyAllowHtmlInDescription,
	dataTestId,
	articleId,
	hideBreadcrumbs = false,
	...props
}) => {
	const isCsmSupportSite = React.useMemo(() => isCSMHelpCenter(getEnv().helpCenterType), []);

	return (
		<div data-testid={dataTestId}>
			{!hideBreadcrumbs &&
				(articleId && isCsmSupportSite ? (
					<React.Suspense
						fallback={<BreadCrumbs {...props} showRequests={showRequestsInBreadcrumbs} />}
					>
						<CsmBreadcrumbs articleId={articleId} />
					</React.Suspense>
				) : (
					<BreadCrumbs {...props} showRequests={showRequestsInBreadcrumbs} />
				))}

			<SubHeadingContainer>
				{!!logoUrl && (
					<IconContainer>
						<AutoScaledImage src={logoUrl} alt="" />
					</IconContainer>
				)}
				<div>
					<UseTypographyMigratedComponent
						migratedComponent={
							<Heading size="large" as="h1">
								{subHeading}
							</Heading>
						}
					>
						<SubHeading>{subHeading}</SubHeading>
					</UseTypographyMigratedComponent>
				</div>
			</SubHeadingContainer>

			{!!description && headerDescription(description, dangerouslyAllowHtmlInDescription)}
		</div>
	);
};

export default PaperHeader;
